// The tests guarding one model, in the spirit of dbt docs: what is checked,
// on which column, and the SQL that checks it.
//
// One honesty rule runs through this component. dbt compiles generic tests
// against a live warehouse, and we have no connection here, so generic SQL
// is RENDERED from the test's own arguments and labelled "canonical shape".
// Singular tests carry their real file contents and are labelled "actual".
// Showing a rendered query as if it were compiled output would make this
// view untrustworthy the first time someone diffed it against dbt.

import { useState } from "react";
import type { LineageGraphNode, LineageTest } from "../types";

const KIND_LABEL: Record<string, string> = {
  not_null: "Not null",
  unique: "Unique",
  relationships: "Foreign key",
  accepted_values: "Accepted values",
  singular: "Custom assertion",
};

// Grouped so a reader scans "what kinds of protection does this model have"
// before drilling into any one test.
const KIND_ORDER = ["singular", "unique", "relationships", "accepted_values", "not_null"];

export function TestCatalogue({ node }: { node: LineageGraphNode }) {
  const [open, setOpen] = useState<string | null>(null);

  if (node.test_count === 0) {
    return (
      <div className="tst-empty">
        <strong>No tests on this model.</strong>
        <p>
          Nothing asserts its grain, its keys, or its relationships. A silent
          change here would not fail the nightly run.
        </p>
      </div>
    );
  }

  const groups = KIND_ORDER.map((kind) => ({
    kind,
    tests: node.tests.filter((t) => t.kind === kind),
  })).filter((g) => g.tests.length > 0);

  const columnsCovered = new Set(
    node.tests.map((t) => t.column).filter((c): c is string => Boolean(c)),
  ).size;

  return (
    <div className="tst">
      <div className="tst-summary">
        <span>
          <strong>{node.test_count}</strong> test
          {node.test_count === 1 ? "" : "s"}
        </span>
        <span>
          <strong>{columnsCovered}</strong> column
          {columnsCovered === 1 ? "" : "s"} covered
        </span>
        {node.documented_columns > 0 && (
          <span>
            <strong>{node.documented_columns}</strong> column
            {node.documented_columns === 1 ? "" : "s"} documented
          </span>
        )}
      </div>

      {groups.map((group) => (
        <section className="tst-group" key={group.kind}>
          <h4>
            <span className={`tst-kind tst-kind-${group.kind}`}>
              {KIND_LABEL[group.kind] ?? group.kind}
            </span>
            <span className="tst-groupcount">{group.tests.length}</span>
          </h4>
          <ul>
            {group.tests.map((test, i) => {
              const id = `${group.kind}-${i}`;
              return (
                <TestRow
                  key={id}
                  test={test}
                  open={open === id}
                  onToggle={() => setOpen(open === id ? null : id)}
                />
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

function TestRow({
  test,
  open,
  onToggle,
}: {
  test: LineageTest;
  open: boolean;
  onToggle: () => void;
}) {
  const args = Object.entries(test.args).filter(([, v]) => v !== undefined);
  return (
    <li className={`tst-row${open ? " is-open" : ""}`}>
      <button className="tst-head" onClick={onToggle} aria-expanded={open}>
        <span className={`tlx-caret${open ? " is-open" : ""}`} aria-hidden="true" />
        <code className="tst-target">{test.name ?? test.column ?? test.target}</code>
        <span className="tst-plain">{test.plain}</span>
        {test.severity !== "error" && (
          <span className="tst-sev">{test.severity}</span>
        )}
      </button>

      {open && (
        <div className="tst-body">
          {args.length > 0 && (
            <dl className="tst-args">
              {args.map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>{Array.isArray(v) ? v.join(", ") : String(v)}</dd>
                </div>
              ))}
            </dl>
          )}
          <div className="tst-sqlhead">
            {test.sql_kind === "actual" ? "Test SQL" : "Canonical shape"}
            {test.sql_kind === "canonical" && (
              <span className="tst-note">
                rendered from the test arguments, not compiled by dbt
              </span>
            )}
            {test.path && <code className="tst-path">{test.path}</code>}
          </div>
          <pre className="tst-sql">{test.sql}</pre>
          <p className="tst-pass">
            The test passes when this query returns no rows.
          </p>
        </div>
      )}
    </li>
  );
}
